import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TAuthFlowRecord } from '@guards/types/auth.flow.types';
import { DateTime } from 'luxon';
import { SESSION_STORAGE_KEYS } from '@core/config/keys/session-storage.key';

const KEY = SESSION_STORAGE_KEYS.AUTH_FLOW;

@Injectable({ providedIn: 'root' })
export class AuthFlowService {
    private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    setToken(token: string) {
        this.write({ token });
    }

    setOtp(otp: string) {
        this.write({ otp });
    }

    getToken(): string | null {
        return this.read()?.token ?? null;
    }

    getOtp(): string | null {
        return this.read()?.otp ?? null;
    }

    hasValid(): boolean {
        return this.read() != null;
    }

    clear() {
        if (this.isBrowser) sessionStorage.removeItem(KEY);
    }

    private read(): TAuthFlowRecord | null {
        if (!this.isBrowser) return null;
        const now = DateTime.now();
        const raw = sessionStorage.getItem(KEY);
        if (!raw) return null;
        try {
            const rec = JSON.parse(raw) as TAuthFlowRecord;
            if (!rec.exp || DateTime.fromISO(rec.exp) < now) {
                sessionStorage.removeItem(KEY);
                return null;
            }
            return rec;
        } catch {
            sessionStorage.removeItem(KEY);
            return null;
        }
    }

    private write(mutation: Partial<TAuthFlowRecord>) {
        if (!this.isBrowser) return;
        const ttl = DateTime.now().plus({ minutes: 60 }).toISO();
        const current = this.read() ?? { exp: ttl };
        const merged: TAuthFlowRecord = { ...current, ...mutation, exp: ttl };
        sessionStorage.setItem(KEY, JSON.stringify(merged));
    }
}
